import { useState } from 'react'
import { Send, Check } from 'lucide-react'
import { Button } from '@shared/ui/Button'
import { LinkTelegramWaitModal } from '@features/link-telegram/ui/LinkTelegramWaitModal'
import { generateLinkTelegram } from '@features/link-telegram/api/linkTelegramApi'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import styles from './OfferTelegramStep.module.css'

interface OfferTelegramStepProps {
  /** Пользователь пропустил связку */
  onSkip: () => void | Promise<void>
  /** Связка Telegram успешно выполнена */
  onLinked: () => void | Promise<void>
}

/**
 * Шаг онбординга: предложение привязать Telegram после регистрации по email.
 * Открываем ссылку бота и показываем модалку ожидания (LinkTelegramWaitModal,
 * как в профиле). После подтверждённой привязки шаг НЕ закрывается сам:
 * попап закрывается, привязка помечается выполненной, юзер остаётся на блоке
 * со статусом «Telegram linked» и сам жмёт «Продолжить» (onLinked).
 * Без привязки доступен явный пропуск «Продолжить регистрацию» (onSkip).
 * @param onSkip - Колбэк при пропуске
 * @param onLinked - Колбэк при переходе дальше после подтверждённой связки
 */
export const OfferTelegramStep = ({ onSkip, onLinked }: OfferTelegramStepProps) => {
  const platform = usePlatform()
  const [waitOpen, setWaitOpen] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [linked, setLinked] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // LinkTelegramWaitModal (та же модалка, что в профиле) сам поллит профиль
  // и зовёт onConfirmed при подтверждённой привязке. После подтверждения
  // НЕ уводим со шага автоматически: закрываем попап, помечаем привязку
  // как выполненную и оставляем юзера на блоке — дальше он сам жмёт
  // «Продолжить». Ручное «Закрыть» просто возвращает на шаг.
  const handleConfirmed = () => {
    setLinked(true)
    setWaitOpen(false)
  }

  const handleLink = async () => {
    setError(null)
    setIsStarting(true)
    // Окно открываем синхронно — иначе браузер блокирует popup после await.
    const tab = prepareAsyncOpenTab(platform)
    try {
      const { link } = await generateLinkTelegram()
      tab.complete(link)
      setWaitOpen(true)
    } catch {
      tab.abort()
      setError('Couldn’t generate the link. Please try again later.')
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <div className={styles.step}>
      <h1 className={styles.title}>Already have a Secret Chat account?</h1>
      <p className={styles.text}>
        Link your Telegram so your existing subscription and data move to this email and you get instant access. Otherwise your access will be limited.
      </p>

      {error && <div className={styles.error}>{error}</div>}

      {linked ? (
        <div className={styles.linked}>
          <Check size={20} strokeWidth={2.5} className={styles.linkedIcon} aria-hidden="true" />
          Telegram linked
        </div>
      ) : (
        <button
          type="button"
          className={styles.tgButton}
          disabled={isStarting}
          onClick={handleLink}
        >
          <Send size={20} strokeWidth={2} className={styles.tgIcon} />
          {isStarting ? 'Opening Telegram…' : 'Link Telegram'}
        </button>
      )}

      {linked ? (
        <Button onClick={() => void onLinked()} block>Continue</Button>
      ) : (
        <Button variant="link" onClick={() => void onSkip()} block>Continue registration</Button>
      )}

      {waitOpen && (
        <LinkTelegramWaitModal
          onConfirmed={handleConfirmed}
          onClose={() => setWaitOpen(false)}
        />
      )}
    </div>
  )
}
